import React, { useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import {
  ordersApi,
  ORDER_STATUS_LABEL,
  OrderStatus,
  VendorOrder,
  VENDOR_NEXT_STATUS,
} from '../../api/orders';
import { formatNgn, formatDateTime } from '../../utils/format';

interface Props {
  order: VendorOrder;
  onClose: () => void;
  onUpdated: (o: VendorOrder) => void;
}

export const OrderDetailDrawer: React.FC<Props> = ({
  order,
  onClose,
  onUpdated,
}) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const nextStatuses = VENDOR_NEXT_STATUS[order.status];
  // Cancel only available pre-shipment (PLACED in backend).
  const canCancel = order.status === 'placed';

  async function handleAdvance(next: OrderStatus) {
    if (
      !window.confirm(
        `Move order ${order.reference} to "${ORDER_STATUS_LABEL[next]}"?`,
      )
    )
      return;
    try {
      setBusy(true);
      const updated = await ordersApi.advance(order.id, next);
      toast.success(`Order moved to ${ORDER_STATUS_LABEL[next]}.`);
      onUpdated(updated);
    } catch (err) {
      toast.error(err, 'Failed to update order.');
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    try {
      setBusy(true);
      const updated = await ordersApi.cancel(
        order.id,
        cancelReason.trim() || undefined,
      );
      toast.success('Order cancelled and buyer refund queued.');
      setShowCancel(false);
      onUpdated(updated);
    } catch (err) {
      toast.error(err, 'Failed to cancel order.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(11, 13, 15, 0.45)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '100%',
          height: '100%',
          background: 'var(--bg-card)',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            background: 'var(--bg-card)',
            zIndex: 1,
          }}
        >
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>{order.reference}</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Placed {formatDateTime(order.placedAt)}
            </div>
          </div>
          <button onClick={onClose} style={{ color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Status + action */}
          <section>
            <Badge>
              {ORDER_STATUS_LABEL[order.status]}
            </Badge>
            {nextStatuses.length > 0 ? (
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                {nextStatuses.map((next) => (
                  <Button
                    key={next}
                    onClick={() => handleAdvance(next)}
                    disabled={busy}
                  >
                    Mark {ORDER_STATUS_LABEL[next]} <ArrowRight size={14} />
                  </Button>
                ))}
                {canCancel ? (
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancel(true)}
                    disabled={busy}
                  >
                    Cancel order
                  </Button>
                ) : null}
              </div>
            ) : order.status === 'delivered' ? (
              <p
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                Waiting on the buyer to confirm receipt. Funds will move from
                escrow to your balance once confirmed.
              </p>
            ) : null}
          </section>

          {/* Items */}
          <section>
            <SectionTitle>Items</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {order.items.map((item, i) => (
                <div
                  key={`${item.productId}-${i}`}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: 12,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  {item.productImageUrl ? (
                    <img
                      src={item.productImageUrl}
                      alt=""
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 'var(--radius-sm)',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        background: 'var(--bg-muted)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{item.productName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Qty {item.qty} × {formatNgn(item.unitPrice)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {formatNgn(item.qty * item.unitPrice)}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: 'var(--bg-muted)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
              }}
            >
              <Row label="Subtotal" value={formatNgn(order.subtotal)} />
              <Row label="Shipping" value={formatNgn(order.shipping)} />
              <Row label="Total" value={formatNgn(order.total)} strong />
            </div>
          </section>

          {/* Shipping address */}
          <section>
            <SectionTitle>Ship to</SectionTitle>
            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                {order.shippingAddress.fullName}
              </div>
              <div>{order.shippingAddress.phone}</div>
              <div>{order.shippingAddress.addressLine}</div>
              <div>
                {order.shippingAddress.city}, {order.shippingAddress.state}
              </div>
              <div>{order.shippingAddress.country}</div>
              {order.shippingAddress.notes ? (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: 'var(--text-muted)',
                  }}
                >
                  Note: {order.shippingAddress.notes}
                </div>
              ) : null}
            </div>
          </section>

          {/* Timeline */}
          <section>
            <SectionTitle>Timeline</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...order.statusHistory].reverse().map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    padding: '6px 0',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {ORDER_STATUS_LABEL[entry.status]}
                    </div>
                    {entry.note ? (
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--text-muted)',
                        }}
                      >
                        {entry.note}
                      </div>
                    ) : null}
                  </div>
                  <div
                    style={{ fontSize: 12, color: 'var(--text-muted)' }}
                  >
                    {formatDateTime(entry.at)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </aside>

      {/* Cancel modal */}
      {showCancel ? (
        <div
          onClick={() => setShowCancel(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(11, 13, 15, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              padding: 24,
              width: '100%',
              maxWidth: 420,
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Cancel order?
            </h3>
            <p
              style={{
                fontSize: 13,
                color: 'var(--text-muted)',
                marginBottom: 16,
              }}
            >
              The buyer's payment will be refunded from escrow. This cannot be
              undone.
            </p>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-secondary)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Reason (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              placeholder="Out of stock, customer requested, …"
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: 14,
                fontFamily: 'var(--font-family)',
                resize: 'vertical',
                outline: 'none',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 16,
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowCancel(false)}
                disabled={busy}
              >
                Keep order
              </Button>
              <Button
                variant="danger"
                onClick={handleCancel}
                loading={busy}
              >
                Cancel order
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 10,
    }}
  >
    {children}
  </h3>
);

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      fontWeight: strong ? 600 : 400,
    }}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
